import { getRouteBehavior } from '@/routeBehavior';

export type PaymentReturnResult = 'success' | 'cancel' | null;

export interface PaymentReturnParamFallback {
    workspaceId?: string | null;
    planCode?: string | null;
    planName?: string | null;
    billingCycle?: string | null;
}

export interface PaymentReturnParams {
    result: PaymentReturnResult;
    workspaceId: string | null;
    planCode: string | null;
    planName: string | null;
    billingCycle: 'monthly' | 'yearly' | null;
    checked: boolean;
    requireActiveStatus?: boolean;
}

export const PAYMENT_RESULT_PARAMS_TO_CLEAR = [
    'pay_result',
    'payment_checked',
    'workspace_id',
    'workspaceId',
    'planCode',
    'planName',
    'billingCycle',
] as const;

const readReturnParamValue = (
    searchParams: URLSearchParams,
    key: string,
    fallback?: string | null,
): string | null => {
    const urlValue = searchParams.get(key)?.trim();
    if (urlValue) {
        return urlValue;
    }

    const fallbackValue = fallback?.trim();
    return fallbackValue || null;
};

const readBillingCycleParam = (
    searchParams: URLSearchParams,
    fallback?: string | null,
): 'monthly' | 'yearly' | null => {
    const value = readReturnParamValue(searchParams, 'billingCycle', fallback);
    if (value === 'monthly' || value === 'yearly') {
        return value;
    }

    return null;
};

export const readPaymentReturnParams = (
    searchParams: URLSearchParams,
    fallback: PaymentReturnParamFallback = {},
): PaymentReturnParams => {
    const rawResult = searchParams.get('pay_result');
    const result = rawResult === 'success' || rawResult === 'cancel'
        ? rawResult
        : null;

    return {
        result,
        workspaceId: readReturnParamValue(searchParams, 'workspace_id', fallback.workspaceId),
        planCode: readReturnParamValue(searchParams, 'planCode', fallback.planCode),
        planName: readReturnParamValue(searchParams, 'planName', fallback.planName),
        billingCycle: readBillingCycleParam(searchParams, fallback.billingCycle),
        checked: searchParams.get('payment_checked') === 'true',
    };
};

export const shouldClearPayResultImmediately = (
    pathname: string,
    payResult: string | null,
): boolean => {
    if (payResult === 'success' && getRouteBehavior(pathname).pageKind === 'onboarding') {
        return false;
    }

    return true;
};

export const shouldFinalizeOnboardingPaymentSuccess = (
    pathname: string,
    payResult: string | null,
    workspaceId?: string | null,
): boolean => {
    return getRouteBehavior(pathname).pageKind === 'onboarding'
        && payResult === 'success'
        && Boolean(workspaceId);
};

export const buildOnboardingPaymentSuccessParams = (
    workspaceId: string,
): Record<string, string> => ({
    onboarding_success: 'true',
    workspace_id: workspaceId,
});

export const shouldSwitchWorkspaceForPaymentResult = (
    payResult: string | null,
    requestedWorkspaceId?: string | null,
    currentWorkspaceId?: string | null,
): boolean => {
    return payResult === 'success'
        && Boolean(requestedWorkspaceId)
        && requestedWorkspaceId !== currentWorkspaceId;
};

export const resolvePaymentWorkspaceAction = (
    payResult: string | null,
    requestedWorkspaceId?: string | null,
    currentWorkspaceId?: string | null,
    isLoadingWorkspaces: boolean = false,
): 'wait' | 'continue' => {
    if (payResult !== 'success' || !requestedWorkspaceId) {
        return 'continue';
    }

    if (isLoadingWorkspaces || !currentWorkspaceId) {
        return 'wait';
    }

    if (requestedWorkspaceId !== currentWorkspaceId) {
        return 'wait';
    }

    return 'continue';
};
